import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { toast } from 'sonner';

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'buyer',
    phone: '',
    location: '',
    bio: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#F9F6F0' }}>
      <div 
        className="w-full max-w-2xl p-8 rounded-md border border-[#E5E0D8]" 
        style={{ backgroundColor: '#FFFFFF' }}
        data-testid="register-form"
      >
        <h1 
          className="text-4xl mb-2 tracking-tight" 
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
        >
          Join eatfromlocal
        </h1>
        <p className="mb-6" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
          Create an account to start buying or selling homemade food
        </p>

        {error && (
          <div 
            className="mb-4 p-3 rounded-md" 
            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
            data-testid="error-message"
          >
            <p style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="password-input"
            />
          </div>

          <div>
            <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
              I want to
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="role-select"
            >
              <option value="buyer">Buy homemade food</option>
              <option value="seller">Sell my homemade food</option>
              <option value="both">Both buy and sell</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                Phone (Optional)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="phone-input"
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State"
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="location-input"
              />
            </div>
          </div>

          {(formData.role === 'seller' || formData.role === 'both') && (
            <div>
              <label className="block mb-1" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                Bio (Tell us about yourself)
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                data-testid="bio-input"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-md py-2"
            style={{ 
              backgroundColor: '#D95A41', 
              fontFamily: 'Manrope', 
              fontWeight: 600 
            }}
            data-testid="register-submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px" style={{ backgroundColor: '#E5E0D8' }}></div>
          <span
            className="uppercase"
            style={{ fontFamily: 'Manrope', fontSize: '11px', color: '#5C5C5C', letterSpacing: '0.2em' }}
          >
            or
          </span>
          <div className="flex-1 h-px" style={{ backgroundColor: '#E5E0D8' }}></div>
        </div>

        <GoogleSignInButton label="Sign up with Google" />

        <p className="mt-6 text-center" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#D95A41', fontWeight: 600 }} data-testid="login-link">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
};